import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  ArrowLeft,
  CheckCircle2,
  Database,
  Download,
  EyeOff,
  Menu,
  RefreshCw,
  Search,
  Upload,
  X
} from 'lucide-react';

const BRAND = {
  primary: 'bg-[#74c476]',
  primaryHover: 'hover:bg-[#5aa85c]',
  secondary: 'bg-[#222222]',
  light: 'bg-stone-50'
};

const VALIDATION_THEME_OPTIONS = [
  { value: 'policy_governance', label: 'Policy governance', desc: 'Decisiones ministeriales, regulaciones, acciones administrativas o implementación de políticas.' },
  { value: 'politics_strategy', label: 'Politics strategy', desc: 'Negociaciones de coalición, estrategia electoral, posicionamiento de partido o comunicación política.' },
  { value: 'personal_or_stylistic', label: 'Personal / stylistic', desc: 'Vida personal del ministro, apariencia u otras características no sustantivas.' },
  { value: 'scandal_or_accountability', label: 'Scandal / accountability', desc: 'Investigaciones judiciales, alegaciones de corrupción o procedimientos formales de rendición de cuentas.' },
  { value: 'not_relevant', label: 'Not relevant', desc: 'El ministro solo se menciona de pasada (listas de asistentes, pies de foto) sin papel sustantivo.' }
];

const VIEW_VALIDATE = 'validate';
const VIEW_DATA = 'data';

const authFetchLocal = async (url, options = {}) => {
  const token = localStorage.getItem('token');
  const headers = { ...(options.headers || {}) };
  if (token) headers.Authorization = `Bearer ${token}`;
  return fetch(url, { ...options, headers });
};

const StatCard = ({ label, value, hint }) => (
  <div className="bg-white border border-stone-200 p-4 rounded-sm shadow-sm">
    <div className="text-xs font-bold text-stone-400 uppercase tracking-wider mb-2">{label}</div>
    <div className="text-2xl font-bold text-stone-900">{value}</div>
    {hint ? <div className="text-sm text-stone-500 mt-1">{hint}</div> : null}
  </div>
);

const highlightText = (content, fullName) => {
  if (!content || !fullName) return content;
  const name = fullName.replace(/_/g, ' ').trim();
  const words = name.split(/\s+/).map((w) => {
    const parts = w.split('-');
    return parts[parts.length - 1];
  });
  let countValid = 0;
  let targetWord = null;
  for (let i = words.length - 1; i >= 0; i--) {
    if (words[i].length > 1) {
      countValid++;
      if (countValid === 2) {
        targetWord = words[i];
        break;
      }
    }
  }
  const terms = [...new Set([name, targetWord].filter((t) => t && t.length >= 2))];
  if (terms.length === 0) return content;
  const escaped = terms.map((t) => t.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'));
  const regex = new RegExp(`(${escaped.join('|')})`, 'gi');
  const parts = content.split(regex);
  if (parts.length === 1) return content;
  return parts.map((part, i) =>
    i % 2 === 1
      ? <mark key={i} style={{ backgroundColor: '#fdff00', padding: '0 1px' }}>{part}</mark>
      : part
  );
};

const CodingList = ({ codings = [] }) => {
  if (!Array.isArray(codings) || codings.length === 0) {
    return <div className="text-sm text-stone-500">Sin codificaciones para este artículo.</div>;
  }

  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead className="text-left text-xs uppercase text-stone-500 border-b border-stone-200">
          <tr>
            <th className="py-2 pr-3">Usuario</th>
            <th className="py-2 pr-3">Tema</th>
            <th className="py-2">Actualizado</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-stone-100">
          {codings.map((coding, index) => (
            <tr key={`${coding?.validatorId || 'na'}-${coding?.updatedAt || index}`}>
              <td className="py-2 pr-3 text-stone-700">{coding?.validatorUsername || '—'}</td>
              <td className="py-2 pr-3 text-stone-800 font-medium">{coding?.selectedThemeLabel || coding?.selectedTheme || '—'}</td>
              <td className="py-2 text-stone-600">{coding?.updatedAt || coding?.createdAt || '—'}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

export default function AutomaticCodingValidationWorkspace({ user, onLogout, onBackToHub, apiUrl }) {
  const [view, setView] = useState(VIEW_VALIDATE);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const [loadingSummary, setLoadingSummary] = useState(false);
  const [loadingTask, setLoadingTask] = useState(false);
  const [saving, setSaving] = useState(false);

  const [summary, setSummary] = useState(null);
  const [dataStatus, setDataStatus] = useState(null);
  const [adminCodings, setAdminCodings] = useState([]);

  const [topicOptions, setTopicOptions] = useState([]);
  const [queue, setQueue] = useState({ total: 0, completed: 0, pending: 0 });
  const [item, setItem] = useState(null);
  const [poolIds, setPoolIds] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(-1);

  const [status, setStatus] = useState('pending');
  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');
  const [selectedTheme, setSelectedTheme] = useState('');

  const [msg, setMsg] = useState(null);
  const [importMsg, setImportMsg] = useState(null);
  const [importingSample, setImportingSample] = useState(false);
  const [downloading, setDownloading] = useState(false);

  const canValidate = user && (user.role === 'admin' || user.role === 'coder');
  const isAdmin = user?.role === 'admin';
  const displayUserName = [user?.name, user?.surname].filter(Boolean).join(' ') || user?.fullName || user?.username || 'User';

  const fetchSummary = useCallback(async () => {
    setLoadingSummary(true);
    try {
      const res = await authFetchLocal(`${apiUrl}/automatic-coding-validation/summary`);
      const data = await res.json();
      if (!res.ok) throw new Error(data?.error || 'No se pudo cargar el resumen');
      setSummary(data?.summary || null);
      setTopicOptions(Array.isArray(data?.topicOptions) ? data.topicOptions : []);
    } catch (error) {
      setSummary(null);
      setTopicOptions([]);
      setMsg({ type: 'error', text: error.message || 'Error cargando resumen' });
    } finally {
      setLoadingSummary(false);
    }
  }, [apiUrl]);

  const fetchDataStatus = useCallback(async () => {
    try {
      const res = await authFetchLocal(`${apiUrl}/automatic-coding-validation/data-status`);
      const data = await res.json();
      if (!res.ok) throw new Error(data?.error || 'No se pudo cargar estado de datos');
      setDataStatus(data || null);
    } catch (error) {
      setDataStatus(null);
      setImportMsg({ type: 'error', text: error.message || 'Error cargando estado de datos' });
    }
  }, [apiUrl]);

  const fetchAdminCodings = useCallback(async () => {
    if (!isAdmin) {
      setAdminCodings([]);
      return;
    }

    try {
      const res = await authFetchLocal(`${apiUrl}/automatic-coding-validation/admin-codings?limit=300`);
      const data = await res.json();
      if (!res.ok) throw new Error(data?.error || 'No se pudieron cargar las codificaciones');
      setAdminCodings(Array.isArray(data?.rows) ? data.rows : []);
    } catch (error) {
      setAdminCodings([]);
      setImportMsg({ type: 'error', text: error.message || 'Error cargando codificaciones' });
    }
  }, [apiUrl, isAdmin]);

  const fetchTask = useCallback(async (options = {}) => {
    const { recordId = '' } = options;
    setLoadingTask(true);
    try {
      const params = new URLSearchParams();
      params.set('status', status);
      if (search.trim()) params.set('search', search.trim());
      if (recordId) params.set('recordId', recordId);

      const res = await authFetchLocal(`${apiUrl}/automatic-coding-validation/task?${params.toString()}`);
      const data = await res.json();
      if (!res.ok) throw new Error(data?.error || 'No se pudo cargar la tarea de validación');

      setQueue(data?.queue || { total: 0, completed: 0, pending: 0 });
      setItem(data?.item || null);
      setPoolIds(Array.isArray(data?.poolIds) ? data.poolIds : []);
      setCurrentIndex(typeof data?.currentIndex === 'number' ? data.currentIndex : -1);
      if (Array.isArray(data?.topicOptions)) {
        setTopicOptions(data.topicOptions);
      }
    } catch (error) {
      setQueue({ total: 0, completed: 0, pending: 0 });
      setItem(null);
      setMsg({ type: 'error', text: error.message || 'Error cargando tarea' });
    } finally {
      setLoadingTask(false);
    }
  }, [apiUrl, search, status]);

  useEffect(() => {
    fetchSummary();
    fetchDataStatus();
    fetchAdminCodings();
  }, [fetchSummary, fetchDataStatus, fetchAdminCodings]);

  useEffect(() => {
    fetchTask();
  }, [fetchTask]);

  useEffect(() => {
    if (!item) {
      setSelectedTheme('');
      return;
    }
    if (item?.currentUserValidation?.selectedTheme) {
      setSelectedTheme(item.currentUserValidation.selectedTheme);
      return;
    }
    setSelectedTheme('');
  }, [item]);

  const pendingPercentage = useMemo(() => {
    if (!queue.total) return 0;
    return Math.round((queue.completed / queue.total) * 100);
  }, [queue.completed, queue.total]);

  const clearMessage = () => setMsg(null);

  const goToPrevious = async () => {
    if (currentIndex <= 0) return;
    await fetchTask({ recordId: poolIds[currentIndex - 1] });
  };

  const goToNext = async () => {
    if (currentIndex >= poolIds.length - 1) return;
    await fetchTask({ recordId: poolIds[currentIndex + 1] });
  };

  const refreshAll = async () => {
    clearMessage();
    await Promise.all([fetchSummary(), fetchTask(), fetchDataStatus(), fetchAdminCodings()]);
  };

  const downloadBlobFromEndpoint = async (endpoint, filename) => {
    setDownloading(true);
    setImportMsg(null);
    try {
      const res = await authFetchLocal(`${apiUrl}${endpoint}`);
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data?.error || 'No se pudo descargar el archivo');
      }
      const blob = await res.blob();
      const href = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = href;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(href);
    } catch (error) {
      setImportMsg({ type: 'error', text: error.message || 'Error al descargar archivo' });
    } finally {
      setDownloading(false);
    }
  };

  const handleImportCsv = async (file) => {
    if (!file) return;
    if (!canValidate || user?.role !== 'admin') {
      setImportMsg({ type: 'error', text: 'Solo admin puede subir archivos CSV.' });
      return;
    }

    setImportingSample(true);
    setImportMsg(null);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const res = await authFetchLocal(`${apiUrl}/automatic-coding-validation/import-sample-csv`, {
        method: 'POST',
        body: formData
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data?.error || 'No se pudo importar el CSV');
      setImportMsg({ type: 'success', text: `Sample CSV importado (${data?.rows || 0} filas).` });
      await Promise.all([fetchSummary(), fetchTask(), fetchDataStatus(), fetchAdminCodings()]);
    } catch (error) {
      setImportMsg({ type: 'error', text: error.message || 'Error importando CSV' });
    } finally {
      setImportingSample(false);
    }
  };

  const handleSaveBlind = async () => {
    if (!canValidate || !item?.recordId) return;

    const normalizedTheme = String(selectedTheme || '').trim();
    if (!normalizedTheme) {
      setMsg({ type: 'error', text: 'Introduce un tema antes de guardar.' });
      return;
    }

    setSaving(true);
    clearMessage();
    try {
      const res = await authFetchLocal(`${apiUrl}/automatic-coding-validation/submit-blind`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ recordId: item.recordId, selectedTheme: normalizedTheme })
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data?.error || 'No se pudo guardar la validacion ciega');

      setMsg({ type: 'success', text: 'Codificación guardada correctamente.' });
      await Promise.all([fetchSummary(), fetchTask(), fetchDataStatus(), fetchAdminCodings()]);
    } catch (error) {
      setMsg({ type: 'error', text: error.message || 'Error al guardar codificación' });
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="flex flex-col md:flex-row h-screen font-sans text-stone-800 bg-stone-50 overflow-hidden relative">
      <div className="md:hidden bg-[#222222] text-white p-4 flex justify-between items-center shadow-md z-50 shrink-0">
        <div className="flex flex-col">
          <h1 className="text-lg font-bold tracking-tight">Automatic Coding Validation</h1>
          <p className="text-xs text-stone-300">Blind review only</p>
        </div>
        <button onClick={() => setMobileMenuOpen(!mobileMenuOpen)} className="text-white p-1">
          {mobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
        </button>
      </div>

      {mobileMenuOpen && (
        <div className="fixed inset-0 bg-black/50 z-30 md:hidden" onClick={() => setMobileMenuOpen(false)} />
      )}

      <aside
        className={`
          fixed inset-y-0 left-0 w-64 ${BRAND.secondary} text-stone-100 flex flex-col shadow-xl z-40 transform transition-transform duration-300 ease-in-out
          ${mobileMenuOpen ? 'translate-x-0' : '-translate-x-full'}
          md:relative md:translate-x-0
        `}
      >
        <div className="p-6 border-b border-stone-700 hidden md:block">
          <h1 className="text-xl font-bold tracking-tight">Automatic Coding</h1>
          <p className="text-xs text-stone-400 mt-1 uppercase tracking-widest">Validation Workspace</p>
        </div>

        <nav className="flex-1 p-4 space-y-1">
          <div className="px-4 py-2 mb-4">
            <div className="text-xs text-stone-400 uppercase font-bold">Logged in as</div>
            <div className="font-medium text-white truncate">{displayUserName}</div>
            <div className="flex justify-between items-center text-xs mt-1">
              <span className="text-green-400 capitalize bg-stone-800 px-1 rounded">{user?.role}</span>
              <button onClick={onLogout} title="Log Out">
                <ArrowLeft size={14} className="text-stone-400 hover:text-red-400" />
              </button>
            </div>
          </div>

          <button
            onClick={() => { setView(VIEW_VALIDATE); setMobileMenuOpen(false); }}
            className={`w-full flex items-center gap-3 px-4 py-3 rounded-sm transition-all text-sm font-medium ${view === VIEW_VALIDATE ? `${BRAND.primary} text-white shadow-lg` : 'text-stone-300 hover:bg-stone-700'}`}
          >
            <EyeOff size={18} />
            <span>Validación ciega</span>
          </button>

          {isAdmin ? (
            <button
              onClick={() => { setView(VIEW_DATA); setMobileMenuOpen(false); }}
              className={`w-full flex items-center gap-3 px-4 py-3 rounded-sm transition-all text-sm font-medium ${view === VIEW_DATA ? `${BRAND.primary} text-white shadow-lg` : 'text-stone-300 hover:bg-stone-700'}`}
            >
              <Database size={18} />
              <span>Data Management</span>
            </button>
          ) : null}

          <div className="pt-4 mt-4 border-t border-stone-700">
            <button
              onClick={() => {
                onBackToHub();
                setMobileMenuOpen(false);
              }}
              className="w-full flex items-center gap-3 px-4 py-3 rounded-sm transition-all text-sm font-medium text-stone-300 hover:bg-stone-700"
            >
              <ArrowLeft size={18} />
              <span>Back to Hub</span>
            </button>
          </div>
        </nav>
      </aside>

      <main className="flex-1 overflow-y-auto p-4 md:p-6 lg:p-8">
        {isAdmin && view === VIEW_DATA ? (
          <div className="space-y-6">
            <header className="mb-2 flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
              <div>
                <h2 className="text-2xl font-bold text-stone-900">Data Management</h2>
                <p className="text-sm text-stone-500 mt-1">Gestión de muestra, codificaciones y exportación.</p>
              </div>
              <button
                onClick={() => Promise.all([fetchDataStatus(), fetchSummary(), fetchAdminCodings()])}
                className="inline-flex items-center gap-2 px-3 py-2 text-sm bg-white border border-stone-300 rounded-sm hover:bg-stone-100"
              >
                <RefreshCw size={16} /> Refrescar estado
              </button>
            </header>

            {importMsg ? (
              <div className={`p-3 rounded-sm border text-sm ${importMsg.type === 'error' ? 'bg-red-50 border-red-200 text-red-700' : 'bg-green-50 border-green-200 text-green-700'}`}>
                {importMsg.text}
              </div>
            ) : null}

            <section className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4">
              <StatCard label="Total artículos" value={dataStatus?.summary?.totalRows ?? '—'} hint="Dataset activo" />
              <StatCard label="Codificaciones totales" value={dataStatus?.summary?.totalCodings ?? '—'} hint="Incluye codificaciones repetidas por artículo" />
              <StatCard label="Artículos codificados" value={dataStatus?.summary?.uniqueValidatedArticles ?? '—'} hint={`Pendientes: ${dataStatus?.summary?.pendingArticles ?? '—'}`} />
              <StatCard label="Tu muestra" value={summary?.sampleSize ?? '—'} hint="Tamaño asignado a tu usuario" />
            </section>

            <section className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <div className="bg-white border border-stone-200 rounded-sm p-5 shadow-sm space-y-4">
                <div className="flex items-center gap-2 text-stone-800 font-bold"><Upload size={18} /> Importar Sample CSV</div>
                <div className="text-xs text-stone-500">Requeridas: columnas id y content.</div>
                <input
                  type="file"
                  accept=".csv"
                  disabled={importingSample || user?.role !== 'admin'}
                  onChange={(e) => handleImportCsv(e.target.files?.[0])}
                  className="w-full border border-stone-300 rounded-sm px-3 py-2 text-sm"
                />
                <div className="text-xs text-stone-500">
                  Activo: {dataStatus?.sources?.sample?.exists ? 'Sí' : 'No'} · Última actualización: {dataStatus?.sources?.sample?.updatedAt || '—'}
                </div>
                <button
                  onClick={() => downloadBlobFromEndpoint('/automatic-coding-validation/export-sample-csv', 'automatic_validation_sample.csv')}
                  className="w-full inline-flex items-center justify-between px-3 py-2 text-sm border border-stone-300 rounded-sm hover:bg-stone-50"
                  disabled={downloading}
                >
                  <span className="inline-flex items-center gap-2"><Database size={14} /> Descargar sample activo</span>
                  <Download size={14} />
                </button>
              </div>

              <div className="bg-white border border-stone-200 rounded-sm p-5 shadow-sm space-y-4">
                <div className="flex items-center gap-2 text-stone-800 font-bold"><Database size={18} /> Exportar codificaciones</div>
                <div className="text-xs text-stone-500">Descarga una fila por codificación (artículo-usuario).</div>
                <button
                  onClick={() => downloadBlobFromEndpoint('/automatic-coding-validation/export-results-csv', `automatic_validation_results_${new Date().toISOString().split('T')[0]}.csv`)}
                  className="inline-flex items-center justify-between w-full gap-2 px-4 py-2 text-sm border border-stone-300 rounded-sm hover:bg-stone-50"
                  disabled={downloading}
                >
                  <span className="inline-flex items-center gap-2"><Database size={14} /> Descargar resultados CSV</span>
                  <Download size={14} />
                </button>
                <div className="text-xs text-stone-500">Solo administradores pueden subir un nuevo sample CSV.</div>
              </div>
            </section>

            <section className="bg-white border border-stone-200 rounded-sm p-5 shadow-sm">
              <div className="flex items-center justify-between gap-3 mb-3">
                <div className="text-stone-800 font-bold">Codificaciones recientes</div>
                <div className="text-xs text-stone-500">Visible para admin</div>
              </div>

              {!isAdmin ? (
                <div className="text-sm text-stone-500">Solo administradores pueden ver esta tabla.</div>
              ) : adminCodings.length === 0 ? (
                <div className="text-sm text-stone-500">No hay codificaciones disponibles.</div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead className="text-left text-xs uppercase text-stone-500 border-b border-stone-200">
                      <tr>
                        <th className="py-2 pr-3">Artículo</th>
                        <th className="py-2 pr-3">Usuario</th>
                        <th className="py-2 pr-3">Tema</th>
                        <th className="py-2">Actualizado</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-stone-100">
                      {adminCodings.map((coding, index) => (
                        <tr key={`${coding?.recordId || index}-${coding?.validatorId || 'na'}-${coding?.updatedAt || index}`}>
                          <td className="py-2 pr-3">
                            <div className="font-medium text-stone-800">{coding?.articleId || '—'}</div>
                            <div className="text-xs text-stone-500">{coding?.minister || '—'}</div>
                          </td>
                          <td className="py-2 pr-3 text-stone-700">{coding?.validatorUsername || '—'}</td>
                          <td className="py-2 pr-3 text-stone-800 font-medium">{coding?.selectedThemeLabel || coding?.selectedTheme || '—'}</td>
                          <td className="py-2 text-stone-600">{coding?.updatedAt || coding?.createdAt || '—'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </section>
          </div>
        ) : (
          <>
            <header className="mb-6 flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
              <div>
                <h2 className="text-2xl font-bold text-stone-900">Validación ciega</h2>
                <p className="text-sm text-stone-500 mt-1">Cada usuario valida su propia muestra aleatoria y puede coexistir con codificaciones de otros usuarios.</p>
              </div>
              <div className="flex flex-wrap gap-2">
                <button
                  onClick={refreshAll}
                  className="inline-flex items-center gap-2 px-3 py-2 text-sm bg-white border border-stone-300 rounded-sm hover:bg-stone-100"
                  disabled={loadingSummary || loadingTask}
                >
                  <RefreshCw size={16} className={loadingSummary || loadingTask ? 'animate-spin' : ''} />
                  Refrescar
                </button>
                <button
                  onClick={goToPrevious}
                  disabled={loadingTask || currentIndex <= 0}
                  className="inline-flex items-center gap-2 px-3 py-2 text-sm bg-white border border-stone-300 rounded-sm hover:bg-stone-100 disabled:opacity-40"
                >
                  <ArrowLeft size={16} />
                  Atrás
                </button>
                <button
                  onClick={goToNext}
                  className={`inline-flex items-center gap-2 px-3 py-2 text-sm text-white rounded-sm ${BRAND.primary} ${BRAND.primaryHover} disabled:opacity-40`}
                  disabled={loadingTask || currentIndex >= poolIds.length - 1}
                >
                  <CheckCircle2 size={16} />
                  Siguiente
                </button>
              </div>
            </header>


            <section className="bg-white border border-stone-200 rounded-sm p-4 shadow-sm mb-6">
              <div className="flex flex-wrap items-end gap-3">
                <div className="min-w-[180px]">
                  <label className="text-xs font-bold uppercase text-stone-500">Estado</label>
                  <select
                    value={status}
                    onChange={(e) => setStatus(e.target.value)}
                    className="mt-1 w-full border border-stone-300 rounded-sm px-3 py-2 text-sm"
                  >
                    <option value="pending">Pendientes</option>
                    <option value="validated">Validados</option>
                    <option value="all">Todos</option>
                  </select>
                </div>

                <div className="flex-1 min-w-[240px]">
                  <label className="text-xs font-bold uppercase text-stone-500">Buscar</label>
                  <div className="mt-1 flex gap-2">
                    <input
                      type="text"
                      value={searchInput}
                      onChange={(e) => setSearchInput(e.target.value)}
                      placeholder="ID, ministro o texto del artículo"
                      className="flex-1 border border-stone-300 rounded-sm px-3 py-2 text-sm"
                    />
                    <button
                      onClick={() => setSearch(searchInput)}
                      className="px-3 py-2 bg-stone-800 text-white rounded-sm hover:bg-stone-700 inline-flex items-center gap-1"
                    >
                      <Search size={14} />
                      Buscar
                    </button>
                  </div>
                </div>

                <div className="shrink-0 self-end pb-0.5 text-sm text-stone-600 whitespace-nowrap">
                  <span className="font-semibold">Progreso:</span>{' '}
                  {queue.completed}/{summary?.totalRows ?? '—'} ({pendingPercentage}%)
                </div>
              </div>
            </section>

            {msg ? (
              <div className={`mb-4 p-3 rounded-sm border text-sm ${msg.type === 'error' ? 'bg-red-50 border-red-200 text-red-700' : 'bg-green-50 border-green-200 text-green-700'}`}>
                {msg.text}
              </div>
            ) : null}

            {loadingTask ? (
              <div className="bg-white border border-stone-200 rounded-sm p-10 text-center text-stone-500">Cargando tarea...</div>
            ) : null}

            {!loadingTask && !item ? (
              <div className="bg-white border border-stone-200 rounded-sm p-10 text-center text-stone-500">
                No hay artículos para esta combinación de filtros.
              </div>
            ) : null}

            {!loadingTask && item ? (
              <div className="flex gap-4 items-start">
                <section className="flex-[4] bg-white border border-stone-200 rounded-sm p-4 shadow-sm min-w-0">
                  <div className="flex flex-wrap gap-x-4 gap-y-1 mb-4 text-sm text-stone-700">
                    <div><span className="font-bold">ID:</span> {item.articleId}</div>
                    <div><span className="font-bold">Fecha:</span> {item.articleDate || '—'}</div>
                    <div><span className="font-bold">Ministro:</span> {item.fullName || item.minister || '—'}</div>
                    <div><span className="font-bold">Ministerio:</span> {item.ministry || '—'}</div>
                  </div>

                  <div className="border border-stone-200 rounded-sm p-4 bg-stone-50 max-h-[520px] overflow-y-auto">
                    <h3 className="text-sm font-bold mb-2 text-stone-700">Artículo completo</h3>
                    <p className="whitespace-pre-wrap text-sm leading-6 text-stone-800">{highlightText(item.content, item.fullName)}</p>
                  </div>
                </section>

                <section className="flex-[1] bg-white border border-stone-200 rounded-sm p-4 shadow-sm min-w-[200px]">
                  <h3 className="text-base font-bold text-stone-900 mb-2">Tu codificación</h3>
                  <p className="text-xs text-stone-500 mb-3 leading-snug">Clasifica el <strong>foco principal</strong> del artículo en una categoría. Elige la más prominente aunque aparezca solo en el 30-40% del texto.</p>
                  <label className="text-xs font-bold uppercase text-stone-500">Tema</label>
                  <div className="mt-2 flex flex-col gap-2">
                    {VALIDATION_THEME_OPTIONS.map((option) => (
                      <button
                        key={`theme-btn-${option.value}`}
                        onClick={() => setSelectedTheme(selectedTheme === option.value ? '' : option.value)}
                        disabled={saving || !canValidate}
                        className={`px-3 py-2 text-sm rounded-sm border transition-colors disabled:opacity-60 text-left ${
                          selectedTheme === option.value
                            ? `${BRAND.primary} text-white border-transparent font-semibold`
                            : 'bg-white text-stone-700 border-stone-300 hover:border-stone-500'
                        }`}
                      >
                        <div className="font-semibold">{option.label}</div>
                        <div className={`text-xs mt-0.5 leading-snug ${selectedTheme === option.value ? 'text-white/80' : 'text-stone-500'}`}>{option.desc}</div>
                      </button>
                    ))}
                  </div>
                  <div className="mt-4">
                    <button
                      onClick={handleSaveBlind}
                      disabled={saving || !canValidate || !item?.recordId}
                      className={`w-full px-4 py-2 rounded-sm text-white text-sm ${BRAND.primary} ${BRAND.primaryHover} disabled:opacity-60`}
                    >
                      Guardar codificación
                    </button>
                  </div>
                  {!canValidate ? (
                    <div className="mt-3 text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-sm p-2">
                      Tu rol no tiene permisos para guardar codificaciones.
                    </div>
                  ) : null}
                </section>
              </div>
            ) : null}
          </>
        )}
      </main>
    </div>
  );
}
